import React from "react";
import { BracesIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import type { WorkflowPayloadMode } from "./workflowRequestUtils";

interface WorkflowPayloadFieldsProps {
  mode: WorkflowPayloadMode;
  onModeChange: (mode: WorkflowPayloadMode) => void;
  inputJson: string;
  onInputJsonChange: (value: string) => void;
  messagesJson: string;
  onMessagesJsonChange: (value: string) => void;
  reuseSession: boolean;
  onReuseSessionChange: (value: boolean) => void;
  showReuseSession?: boolean;
  showDescription?: boolean;
  showEditor?: boolean;
  variant?: "studio" | "forge";
  disabled?: boolean;
}

const WorkflowPayloadFields: React.FC<WorkflowPayloadFieldsProps> = ({
  mode,
  onModeChange,
  inputJson,
  onInputJsonChange,
  messagesJson,
  onMessagesJsonChange,
  reuseSession,
  onReuseSessionChange,
  showReuseSession = true,
  showDescription = true,
  showEditor = true,
  variant = "studio",
  disabled = false,
}) => {
  const isStudio = variant === "studio";

  return (
    <div className="space-y-4">
      {showDescription && isStudio && (
        <p className="text-sm text-muted-foreground rounded-md border border-border bg-muted/30 px-3 py-2">
          Workflows run as discrete API calls. Each run appears in this thread for your notes, but by
          default the server uses a new session per run so nothing is chained unless you enable reuse
          below. Use{" "}
          <span className="font-medium text-foreground">New Conversation</span> to clear the thread
          and start over. The same JSON input style is being added for agents in Studio so both feel
          consistent.
        </p>
      )}

      {showDescription && !isStudio && (
        <p className="text-sm text-muted-foreground">
          Prefer <span className="font-medium text-foreground">JSON input</span> for most workflows;
          use <span className="font-medium text-foreground">Chat message</span> for freeform text (or paste
          a JSON messages array). Session reuse matches the Sessions tab when you run again with the same session id.
        </p>
      )}

      <div className="space-y-2">
        <Label className="text-sm font-gc-med">Request shape</Label>
        <RadioGroup
          value={mode}
          onValueChange={(v) => onModeChange(v as WorkflowPayloadMode)}
          className="flex flex-wrap gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="input" id="wf-payload-input" disabled={disabled} />
            <Label htmlFor="wf-payload-input" className="font-normal cursor-pointer">
              JSON input
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="messages" id="wf-payload-messages" disabled={disabled} />
            <Label htmlFor="wf-payload-messages" className="font-normal cursor-pointer">
              Chat message
            </Label>
          </div>
        </RadioGroup>
      </div>

      {showEditor && (mode === "input" ? (
        <div className="space-y-2">
          <div className="flex items-end justify-between">
            <Label htmlFor="wf-json-input">Input object</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-2 text-xs text-muted-foreground"
              disabled={disabled}
              onClick={() => {
                try {
                  const parsed = JSON.parse(inputJson);
                  onInputJsonChange(JSON.stringify(parsed, null, 2));
                } catch { /* ignore invalid JSON */ }
              }}
            >
              <BracesIcon className="h-3 w-3" />
              Pretty
            </Button>
          </div>
          <Textarea
            id="wf-json-input"
            className="font-mono text-sm min-h-[140px]"
            value={inputJson}
            onChange={(e) => onInputJsonChange(e.target.value)}
            disabled={disabled}
            spellCheck={false}
          />
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="wf-json-messages">Message</Label>
          <Textarea
            id="wf-json-messages"
            className="text-sm min-h-[80px]"
            placeholder="Type a message, or paste a JSON messages array…"
            value={messagesJson}
            onChange={(e) => onMessagesJsonChange(e.target.value)}
            disabled={disabled}
          />
          <p className="text-xs text-muted-foreground">
            Plain text is wrapped as a user message. You can also paste a JSON array like{" "}
            <code className="text-xs bg-muted px-1 rounded">{"[{\"role\":\"user\",\"content\":\"...\"}]"}</code>.
          </p>
        </div>
      ))}

      {showReuseSession && (
        <div className="flex items-start gap-2">
          <Checkbox
            id="wf-reuse-session"
            checked={reuseSession}
            onCheckedChange={(c) => onReuseSessionChange(c === true)}
            disabled={disabled}
            className="mt-0.5"
          />
          <div className="space-y-0.5">
            <Label htmlFor="wf-reuse-session" className="font-normal cursor-pointer">
              Reuse workflow session across runs
            </Label>
            <p className="text-xs text-muted-foreground">
              When checked, subsequent runs in this thread share the same{" "}
              <code className="text-xs bg-muted px-1 rounded">session_id</code> (multi-turn workflow
              semantics). When unchecked, every run uses a new session.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowPayloadFields;
